import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, SaveIcon } from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { useSidebar } from '@/shared/ui/layout/SidebarContext';
import { Button } from '@/shared/ui/buttons/Button';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { extractErrorMessage } from '@/shared/api/errors';
import { queryKeys } from '@/shared/api/queryKeys';
import { useParcelDetail } from '@/entities/parcels';
import {
  apiParcelsProductsDelete,
  apiParcelsProductsPost,
  apiParcelsProductsPut,
  useParcelProducts,
} from '@/entities/parcels-products';
import { useCategories } from '@/entities/categories';
import { usePinflLimit } from '@/entities/pinfl';
import { cn } from '@/shared/utils/cn';
import {
  FeaturesDeclarationPinflLimitBar,
  FeaturesDeclarationProductFormItem,
  emptyProduct,
  productSchema,
  productTotal,
  type DraftProduct,
} from '@/features/declaration';

export const WidgetsParcelDeclarationMain = () => {
  const { t } = useTranslation('declaration');
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { collapsed } = useSidebar();

  const parcelQuery = useParcelDetail(id);
  const productsQuery = useParcelProducts(id);
  const categoriesQuery = useCategories();
  const pinflQuery = usePinflLimit(parcelQuery.data?.pinfl ?? '', id);

  const [drafts, setDrafts] = useState<DraftProduct[]>([emptyProduct()]);

  useEffect(() => {
    if (productsQuery.data && productsQuery.data.length > 0) {
      setDrafts(
        productsQuery.data.map((p) => ({
          client_id: p.id,
          server_id: p.id,
          category_id: p.category_id,
          subcategory_id: p.subcategory_id,
          full_name: p.full_name,
          quantity: p.quantity,
          price_per_item: p.price_per_item,
        })),
      );
    }
  }, [productsQuery.data]);

  const declaredTotal = useMemo(
    () => drafts.reduce((sum, d) => sum + productTotal(d), 0),
    [drafts],
  );

  const validateDrafts = (): { ok: boolean; message?: string } => {
    if (drafts.length === 0) return { ok: false, message: t('validation.atLeastOne') };
    for (let i = 0; i < drafts.length; i += 1) {
      const result = productSchema.safeParse(drafts[i]);
      if (!result.success) {
        const firstIssue = result.error.issues[0];
        return {
          ok: false,
          message: t('validation.productPrefix', {
            index: i + 1,
            message: firstIssue?.message ? t(firstIssue.message) : t('validation.check'),
          }),
        };
      }
    }
    return { ok: true };
  };

  const syncProducts = useMutate({
    api: async () => {
      const existing = productsQuery.data ?? [];
      const existingIds = new Set(existing.map((e) => e.id));
      const keptIds = new Set(drafts.map((d) => d.server_id).filter((s): s is string => Boolean(s)));

      for (const existingProduct of existing) {
        if (!keptIds.has(existingProduct.id)) {
          await apiParcelsProductsDelete(id, existingProduct.id);
        }
      }

      for (const draft of drafts) {
        const body = {
          category_id: draft.category_id,
          subcategory_id: draft.subcategory_id,
          full_name: draft.full_name,
          quantity: draft.quantity,
          price_per_item: draft.price_per_item,
        };
        if (draft.server_id && existingIds.has(draft.server_id)) {
          await apiParcelsProductsPut(id, draft.server_id, body);
        } else {
          await apiParcelsProductsPost(id, body);
        }
      }
    },
    invalidateKeys: [queryKeys.parcels.products(id), queryKeys.parcels.detail(id), queryKeys.parcels.all, queryKeys.pinfl.all],
    onSuccess: () => {
      toast.success(t('toast.saved'));
      navigate(`/parcels/${id}`);
    },
    onError: (_e, message) => toast.error(message),
  });

  const handleSave = (): void => {
    const v = validateDrafts();
    if (!v.ok) {
      toast.error(v.message ?? t('validation.incomplete'));
      return;
    }
    syncProducts.mutate(undefined);
  };

  if (parcelQuery.isLoading || productsQuery.isLoading || categoriesQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (parcelQuery.error) {
    return (
      <div className="p-4">
        <ErrorState
          message={extractErrorMessage(parcelQuery.error, t('parcelDetail:notFound'))}
          onRetry={() => void parcelQuery.refetch()}
        />
      </div>
    );
  }

  if (categoriesQuery.error) {
    return (
      <div className="p-4">
        <ErrorState
          message={extractErrorMessage(categoriesQuery.error, t('loadCategoriesError'))}
          onRetry={() => void categoriesQuery.refetch()}
        />
      </div>
    );
  }

  const categories = categoriesQuery.data ?? [];
  // Declaration editing is gated on the workflow status: only IN_PROGRESS parcels
  // (= before the user has confirmed shipping) can have products added or edited.
  const editable = (parcelQuery.data?.client_status ?? 'IN_PROGRESS') === 'IN_PROGRESS';

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={parcelQuery.data?.name}
        showBack
        backTo={`/parcels/${id}`}
      />
      <FeaturesDeclarationPinflLimitBar
        limit={pinflQuery.data ?? null}
        currentDeclared={declaredTotal}
        isLoading={pinflQuery.isFetching}
      />

      <div className="flex flex-col gap-3 p-4 md:pb-36 lg:gap-4 lg:p-6">
        {drafts.map((draft, index) => (
          <FeaturesDeclarationProductFormItem
            key={draft.client_id}
            index={index}
            product={draft}
            categories={categories}
            canRemove={drafts.length > 1 && editable}
            onChange={(next) =>
              setDrafts((prev) => prev.map((p) => (p.client_id === draft.client_id ? next : p)))
            }
            onRemove={() =>
              setDrafts((prev) => prev.filter((p) => p.client_id !== draft.client_id))
            }
          />
        ))}

        {editable && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setDrafts((prev) => [...prev, emptyProduct()])}
            leftIcon={<PlusIcon className="h-4 w-4" />}
            block
          >
            {t('addProduct')}
          </Button>
        )}

        {editable && (
          <div
            className={cn(
              'mt-2 border-t border-slate-200 bg-white/95 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur transition-[margin] md:fixed md:inset-x-0 md:bottom-0 md:z-20 md:mt-0',
              collapsed ? 'md:ml-16' : 'md:ml-60 lg:ml-64',
            )}
          >
            <div className="mx-auto flex max-w-3xl justify-end lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl lg:px-6">
              <Button
                type="button"
                onClick={handleSave}
                loading={syncProducts.isPending}
                leftIcon={<SaveIcon className="h-4 w-4" />}
                className="w-full sm:w-auto"
              >
                {t('save')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
